import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom Electron Title Bar Component
 * Displays on every page with:
 * - macOS traffic lights (close, minimize, fullscreen) on the left
 * - Page title in the center
 * - Double-click to maximize/restore
 */
export default function TitleBar() {
  const location = useLocation();
  // eslint-disable-next-line no-unused-vars
  const [isMaximized, setIsMaximized] = useState(false);
  
  // Check if running in Electron
  const isElectron = window.electronAPI !== undefined;

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    
    if (path === '/' || path === '/home') return 'Lenify';
    if (path === '/signin') return 'Sign In';
    if (path === '/signup') return 'Sign Up';
    if (path === '/dashboard') return 'Dashboard';
    if (path === '/download') return 'Download';
    if (path === '/forgot-password') return 'Forgot Password';
    if (path === '/reset-password') return 'Reset Password';
    if (path === '/privacy-policy') return 'Privacy Policy';
    if (path === '/terms-of-service') return 'Terms of Service';
    if (path === '/activate') return 'Activate Account';
    if (path === '/activation-pending') return 'Activation Pending';
    if (path.startsWith('/projects/') && path.includes('/ide')) return 'IDE';
    if (path.startsWith('/projects/') && path.includes('/planning')) return 'Project Planning';
    if (path.startsWith('/projects/')) return 'Project';
    if (path.startsWith('/invitation/')) return 'Invitation';
    
    return 'Lenify';
  };

  // Update maximized state
  useEffect(() => {
    if (!isElectron) return;
    
    const checkMaximized = async () => {
      try {
        const maximized = await window.electronAPI.isMaximized();
        setIsMaximized(maximized);
      } catch (e) {
        // Ignore errors
      }
    };
    
    checkMaximized();
    
    // Listen for window state changes
    const interval = setInterval(checkMaximized, 500);
    return () => clearInterval(interval);
  }, [isElectron]);

  // Handle double-click to toggle maximize
  const handleDoubleClick = async () => {
    if (!isElectron) return;
    try {
      const newState = await window.electronAPI.toggleMaximize();
      setIsMaximized(newState);
    } catch (e) {
      // Ignore errors
    }
  };

  // Don't render in browser (non-Electron)
  if (!isElectron) return null;

  return (
    <div
      className="title-bar h-9 flex items-center justify-center select-none relative"
      style={{
        background: 'linear-gradient(180deg, #2a2a2d 0%, #232326 100%)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        WebkitAppRegion: 'drag',
        WebkitUserSelect: 'none',
        userSelect: 'none',
      }}
      onDoubleClick={handleDoubleClick}
    >
      {/* Left spacer for macOS traffic lights */}
      <div 
        className="absolute left-0 top-0 bottom-0 w-20"
        style={{ WebkitAppRegion: 'no-drag' }}
      />

      {/* Center - Page Title */}
      <div className="flex items-center gap-2">
        {/* Small Logo Icon */}
        <div 
          className="w-4 h-4 rounded flex items-center justify-center"
          style={{ 
            background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
          }}
        >
          <svg className="w-2.5 h-2.5" viewBox="0 0 100 100" fill="none">
            <g transform="translate(15, 10)">
              <path
                d="M30 15 C30 55, 30 65, 45 65 L60 65"
                stroke="white"
                strokeWidth="14"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
          </svg>
        </div>
        
        {/* Title Text */}
        <span 
          className="text-xs font-medium tracking-wide"
          style={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            letterSpacing: '0.02em'
          }}
        >
          {getPageTitle()}
        </span>
      </div>

      {/* Right spacer for balance */}
      <div className="absolute right-0 top-0 bottom-0 w-20" />
    </div>
  );
}
